/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Select,
  InputNumber,
  Alert,
  Steps,
  Divider,
  Result,
  DatePicker,
  Spin,
} from "antd";
import {
  CheckCircleOutlined,
  CreditCardOutlined,
  LockOutlined,
  SafetyOutlined,
  CalendarOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import {
  reservationsApi,
  tripsApi,
  schedulesApi,
  boatApi,
  paymentsApi,
} from "../../apis";
import { hasPermission } from "../../utils/permissions";

const { Option } = Select;

// ══════════════════════════════════════════════════════════════════════════════
const AddReservation = () => {
  const history = useHistory();
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  /* ── UI state ── */
  const [loading, setLoading] = useState(false);
  const [menu, setMenu] = useState(false);

  /* ── Data state ── */
  const [trips, setTrips] = useState([]);
  const [decks, setDecks] = useState([]);
  const [tripSchedules, setTripSchedules] = useState([]); // Store full schedule objects
  const [availability, setAvailability] = useState(null);
  const [priceInfo, setPriceInfo] = useState(null); // { pricePerSeat, total } for preview

  /* ── Form ── */
  const [form, setForm] = useState({
    tripId: "",
    tripDateId: "",
    tripDateId: "",
    deckId: "",
    adultSeats: 1,
    childSeats: 0,
    guestName: "",
    guestEmail: "",
    guestPhone: "",
  });
  const [selectedDeckPrices, setSelectedDeckPrices] = useState(null);

  /* ── Local phone number state (without +20) ── */
  const [localPhoneNumber, setLocalPhoneNumber] = useState("");

  /* ── After creation ── */
  const [createdReservation, setCreatedReservation] = useState(null);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);

        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
        console.log(
          "AddReservation - User permissions:",
          normalizedPermissions,
        );
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions using standardized utility
  const canCreateReservations = hasPermission(
    userPermissions,
    "reservations.create",
  );
  const canViewTrips = hasPermission(userPermissions, "trips.view");
  const canViewSchedules = hasPermission(userPermissions, "schedules.view");

  /* ── Load trips + decks ── */
  useEffect(() => {
    if (canCreateReservations) {
      Promise.all([tripsApi.getAll({ limit: 100 }), boatApi.getAllDecks()])
        .then(([t, d]) => {
          setTrips(t.data || []);
          setDecks(Array.isArray(d) ? d : []);
        })
        .catch(() => message.error("Failed to load data"));
    }
  }, [canCreateReservations]);

  /* ── Load schedule dates when trip changes ── */
  const handleTripChange = async (tripId) => {
    setForm((f) => ({ ...f, tripId, tripDateId: "", deckId: "" }));
    setTripSchedules([]);
    setAvailability(null);
    setPriceInfo(null);
    if (!tripId) return;
    try {
      const r = await schedulesApi.getByTrip(tripId, { limit: 200 });
      // Store only active schedules
      const activeSchedules = (r.data || []).filter(
        (d) => d.status === "active",
      );
      setTripSchedules(activeSchedules);
    } catch {
      message.error("Failed to load schedule dates");
    }
  };

  /* ── Handle date selection ── */
  const handleDateChange = (date, dateString) => {
    if (!date) {
      setForm((f) => ({ ...f, tripDateId: "" }));
      setAvailability(null);
      return;
    }

    // Find the schedule that matches the selected date
    const selectedSchedule = tripSchedules.find(
      (schedule) => schedule.date === dateString,
    );
    if (selectedSchedule) {
      setForm((f) => ({ ...f, tripDateId: selectedSchedule.id }));
      // Check availability if deck is already selected
      if (form.deckId) {
        checkAvailability(form.deckId, selectedSchedule.id);
      }
    }
  };

  /* ── Check availability when deck or date changes ── */
  const checkAvailability = async (deckId, tripDateId) => {
    const d = deckId || form.deckId;
    const td = tripDateId || form.tripDateId;
    if (!d || !td) return;
    try {
      const r = await reservationsApi.checkAvailability(td, d);
      setAvailability(r.availableSeats);

      // Also set the selected deck prices for preview
      const schedule = tripSchedules.find((s) => s.id === td);
      const override = (schedule?.deckPricings || []).find(
        (p) => p.deckId === d,
      );
      const tripDeck = (trips.find((t) => t.id === form.tripId)?.tripDecks || []).find(
        (td) => td.deckId === d,
      );

      if (override) {
        setSelectedDeckPrices({
          adultPrice: override.adultPrice,
          childPrice: override.childPrice,
        });
      } else if (tripDeck) {
        setSelectedDeckPrices({
          adultPrice: tripDeck.adultPrice,
          childPrice: tripDeck.childPrice,
        });
      } else {
        setSelectedDeckPrices(null);
      }
    } catch {
      setAvailability(null);
      setSelectedDeckPrices(null);
    }
  };

  /* ── Handle phone input change (11 digits max) ── */
  const handlePhoneChange = (e) => {
    const formatPhoneNumber = (value) => {
      if (!value) return "";
      let digits = value.replace(/\D/g, "");
      if (digits.length > 11) {
        digits = digits.slice(0, 11);
      }
      return digits;
    };

    const digits = formatPhoneNumber(e.target.value);
    setLocalPhoneNumber(digits);
    // Store the full phone number with +20 prefix in the form
    const fullPhoneNumber = digits ? `+20${digits}` : "";
    setForm((f) => ({ ...f, guestPhone: fullPhoneNumber }));
  };

  /* ── Disable dates that are not in tripSchedules ── */
  const disabledDate = (current) => {
    if (!tripSchedules.length || !form.tripId) return true;

    // Get all available dates from tripSchedules
    const availableDates = tripSchedules.map((schedule) => schedule.date);

    // Check if current date is in availableDates
    const dateStr = current.format("YYYY-MM-DD");
    return !availableDates.includes(dateStr);
  };

  /* ── Get the selected date value for the DatePicker ── */
  const getSelectedDateValue = () => {
    if (!form.tripDateId) return null;
    const selectedSchedule = tripSchedules.find(
      (s) => s.id === form.tripDateId,
    );
    if (selectedSchedule && selectedSchedule.date) {
      return dayjs(selectedSchedule.date);
    }
    return null;
  };

  /* ── Create the reservation (admin only) ── */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateReservations) {
      message.error("You don't have permission to create reservations");
      return;
    }

    if (!form.tripDateId || !form.deckId)
      return message.error("Please select a date and deck");
    if (form.adultSeats + form.childSeats < 1) return message.error("Select at least 1 seat");

    const totalSeats = form.adultSeats + form.childSeats;
    if (availability !== null && totalSeats > availability) {
      return message.error(`Only ${availability} seats available on this deck`);
    }

    // Validate phone number if provided (11 digits after +20)
    if (form.guestPhone) {
      const localNumber = form.guestPhone.replace(/^\+20/, "");
      if (!/^\d{11}$/.test(localNumber)) {
        return message.error(
          "Invalid phone number. Must be 11 digits after +20 (e.g., 10012345678)",
        );
      }
    }

    const payload = {
      tripDateId: form.tripDateId,
      deckId: form.deckId,
      adultSeats: form.adultSeats,
      childSeats: form.childSeats,
      guestName: form.guestName || undefined,
      guestEmail: form.guestEmail || undefined,
      guestPhone: form.guestPhone || undefined,
    };

    try {
      setLoading(true);

      // Admin: instant confirm, no payment flow
      const reservation = await reservationsApi.adminCreate(payload);
      message.success(
        "Reservation created successfully! The reservation is confirmed and ticket has been issued.",
      );
      history.push(`/reservations/${reservation.id}`);
    } catch (e) {
      const msg = e.response?.data?.message;
      if (e.response?.status === 403) {
        message.error("You don't have permission to create reservations");
      } else if (Array.isArray(msg)) {
        msg.forEach((m) => message.error(m));
      } else {
        message.error(msg || "Failed to create reservation");
      }
    } finally {
      setLoading(false);
    }
  };

  /* ── Helpers ── */
  const selectedTrip = trips.find((t) => t.id === form.tripId);
  const selectedDeck = decks.find((d) => d.id === form.deckId);
  const selectedSchedule = tripSchedules.find((d) => d.id === form.tripDateId);

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for creating reservations
  if (!canCreateReservations) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to create reservations.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> reservations.create
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                  <p className="mt-3">
                    <Button
                      type="primary"
                      onClick={() => history.push("/reservations")}
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                    >
                      Go to Reservations
                    </Button>
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════════════ */
  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              {/* ── Page header ── */}
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Create Admin Reservation</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel me-2"
                    onClick={() => history.goBack()}
                    disabled={loading}
                  >
                    Back
                  </button>
                </div>
              </div>

              <Alert
                message="Admin Reservation Creation"
                description="This is an admin-only feature. Reservations created here are automatically confirmed and tickets are issued immediately. This is ideal for phone bookings or walk-in customers."
                type="info"
                showIcon
                className="mb-4"
              />

              <form onSubmit={handleSubmit}>
                {/* Trip selection */}
                <div className="form-group-item">
                  <h5 className="form-title">Trip Selection</h5>
                  <div className="row">
                    {/* Trip */}
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Trip <span className="text-danger">*</span>
                        </label>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select a trip"
                          value={form.tripId || undefined}
                          onChange={handleTripChange}
                          showSearch
                          optionFilterProp="children"
                          allowClear
                          disabled={loading}
                        >
                          {trips.map((t) => (
                            <Option key={t.id} value={t.id}>
                              {t.name}
                            </Option>
                          ))}
                        </Select>
                      </div>
                    </div>

                    {/* Date - Now using DatePicker */}
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Date <span className="text-danger">*</span>
                        </label>
                        <DatePicker
                          style={{ width: "100%" }}
                          placeholder="Select date"
                          value={getSelectedDateValue()}
                          onChange={handleDateChange}
                          disabled={!form.tripId || loading}
                          disabledDate={disabledDate}
                          format="YYYY-MM-DD"
                          allowClear
                          suffixIcon={<CalendarOutlined />}
                        />
                        {tripSchedules.length === 0 && form.tripId && (
                          <small className="form-text text-warning d-block mt-1">
                            No available dates for this trip
                          </small>
                        )}
                        {tripSchedules.length > 0 && form.tripId && (
                          <small className="form-text text-muted d-block mt-1">
                            {tripSchedules.length} available date
                            {tripSchedules.length !== 1 ? "s" : ""} to choose
                            from
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Deck */}
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Deck <span className="text-danger">*</span>
                        </label>
                        <Select
                          style={{ width: "100%" }}
                          placeholder="Select deck"
                          value={form.deckId || undefined}
                          onChange={(v) => {
                            setForm((f) => ({ ...f, deckId: v }));
                            checkAvailability(v, undefined);
                          }}
                          disabled={loading}
                          allowClear
                        >
                          {decks.map((d) => (
                            <Option key={d.id} value={d.id}>
                              {d.name}
                            </Option>
                          ))}
                        </Select>
                        {availability !== null && (
                          <small
                            className={`form-text ${
                              availability > 0 ? "text-success" : "text-danger"
                            }`}
                          >
                            {availability > 0
                              ? `✓ ${availability} seats available`
                              : "✗ No seats available on this date"}
                          </small>
                        )}
                      </div>
                    </div>

                    {/* Seats */}
                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>
                          Adult Seats <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          max={availability !== null ? availability - form.childSeats : 999}
                          value={form.adultSeats}
                          onChange={(v) => setForm((f) => ({ ...f, adultSeats: v }))}
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-lg-2 col-md-4">
                      <div className="input-block mb-3">
                        <label>
                          Child Seats
                        </label>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0}
                          max={availability !== null ? availability - form.adultSeats : 999}
                          value={form.childSeats}
                          onChange={(v) => setForm((f) => ({ ...f, childSeats: v }))}
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  {selectedDeckPrices && (
                    <div
                      className="mt-3 p-3"
                      style={{
                        background: "#FDF3E3",
                        borderRadius: 12,
                        border: "1px solid #F5ECD7",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <div style={{ fontSize: 13, color: "#7B4D12", fontWeight: 600 }}>
                            Total Price Preview
                          </div>
                          <div style={{ fontSize: 12, color: "#9ca3af" }}>
                            {form.adultSeats} Adults x EGP {selectedDeckPrices.adultPrice.toLocaleString()}
                            {form.childSeats > 0 &&
                              ` + ${form.childSeats} Children x EGP ${selectedDeckPrices.childPrice.toLocaleString()}`}
                          </div>
                        </div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: "#7B4D12" }}>
                          EGP {(
                            form.adultSeats * selectedDeckPrices.adultPrice +
                            form.childSeats * selectedDeckPrices.childPrice
                          ).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Guest info */}
                <div className="form-group-item mt-2">
                  <h5 className="form-title">Customer Information</h5>
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Full Name{" "}
                          <span className="text-muted">(optional)</span>
                        </label>
                        <input
                          className="form-control"
                          placeholder="e.g. Ahmed Hassan"
                          value={form.guestName}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              guestName: e.target.value,
                            }))
                          }
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Email <span className="text-muted">(optional)</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="email@example.com"
                          value={form.guestEmail}
                          onChange={(e) =>
                            setForm((f) => ({
                              ...f,
                              guestEmail: e.target.value,
                            }))
                          }
                          disabled={loading}
                        />
                        <small className="text-muted">
                          Ticket will be sent to this email if provided
                        </small>
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Phone <span className="text-muted">(optional)</span>
                        </label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <span className="me-1">🇪🇬</span> +20
                          </span>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="10012345678"
                            value={localPhoneNumber}
                            onChange={handlePhoneChange}
                            disabled={loading}
                          />
                        </div>
                        <small className="text-muted d-block mt-1">
                          Enter 11 digits after +20 (e.g., 10012345678)
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="form-group-item border-0">
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary cancel me-2"
                      onClick={() => history.goBack()}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading || availability === 0}
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Creating Reservation...
                        </>
                      ) : (
                        "Create Reservation & Issue Ticket"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddReservation;